import { useForm, useWatch } from 'react-hook-form';
import { useMemo, useCallback, useState, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar, Clock, Users, Shield, HelpCircle, Plus, Trash2, CalendarIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as UICalendar } from '@/components/ui/calendar';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import type { 
  CreateLeavePolicyInput, 
  UpdateLeavePolicyInput, 
  AccrualMethod, 
  HourlyAccrualType,
  FixedAccrualFrequency,
  FixedAccrualReleaseType,
  WorkingHoursCalculationType,
  ProRataCalculationType,
  TenureRule
} from '@/services/leave/leavePolicyTypes';

const tenureRuleSchema = z.object({
  years_of_service: z.number().min(0),
  additional_days: z.number().min(0).optional(),
  multiplier: z.number().min(0).optional(),
});

const accrualRuleSchema = z.object({
  // Does Not Accrue Settings
  does_not_deduct: z.boolean().default(false),
  
  
  // Hourly Worked Settings
  hourly_accrual_type: z.enum(['per_hour_worked', 'per_threshold_worked']).optional(),
  hours_per_hour_worked: z.number().min(0).optional(),
  accrual_rate_type: z.enum(['hours', 'percentage']).optional(),
  accrual_rate: z.number().min(0).optional(),
  include_overtime_hours: z.boolean().default(false),
  threshold_hours_to_work: z.number().min(0).optional(),
  threshold_leave_amount: z.number().min(0).optional(),
  threshold_leave_unit: z.enum(['hours', 'days']).optional(),
  
  
  // Fixed Accrual Settings
  fixed_accrual_amount: z.number().min(0).optional(),
  fixed_accrual_unit: z.enum(['hours', 'days']).optional(),
  fixed_accrual_frequency: z.enum(['weekly', 'monthly', 'quarterly', 'yearly']).optional(),
  fixed_accrual_release_type: z.enum(['specific_date', 'hire_date']).optional(),
  fixed_accrual_specific_date: z.string().optional(),
  fixed_accrual_pro_rata: z.boolean().default(false),
  pro_rata_calculation_type: z.enum(['days', 'hours', 'percentage']).default('days'),
  pro_rata_rounding_direction: z.enum(['up', 'down', 'nearest']).default('nearest'),
  
  
  
  // Advanced Rules
  enable_pro_rata: z.boolean().default(true),
  pro_rata_start_date: z.string().optional(),
  
  // Tenure-based rules
  enable_tenure_rules: z.boolean().default(false),
  tenure_rules: z.array(tenureRuleSchema).default([]),
  
  // Balance Management
  max_carry_over_days: z.number().min(0).optional(),
  max_carry_over_hours: z.number().min(0).optional(),
  balance_cap_days: z.number().min(0).optional(),
  balance_cap_hours: z.number().min(0).optional(),
  allow_negative_balance: z.boolean().default(false),
  max_negative_days: z.number().optional(),
  negative_balance_action: z.enum(['block_request', 'allow_and_notify']).default('block_request'),
  
  // Max Yearly Accrual
  enable_max_yearly_accrual: z.boolean().default(false),
  max_yearly_accrual_days: z.number().min(0).optional(),
  
  // Balance Cap
  enable_balance_cap: z.boolean().default(false),
  
  // Rollover/Reset Management
  rollover_type: z.enum(['rollover', 'reset']).default('rollover'),
  rollover_amount_type: z.enum(['entire', 'partial']).default('entire'),
  rollover_partial_amount: z.number().min(0).optional(),
  rollover_date_type: z.enum(['specific_date', 'hire_date']).default('hire_date'),
  rollover_specific_date: z.string().optional(),
});

const policySchema = z.object({
  name: z.string().min(1, 'Policy name is required'),
  description: z.string().optional(),
  leave_type: z.string().min(1, 'Leave type is required'),
  accrual_method: z.enum(['does_not_accrue', 'hourly_worked', 'fixed_accrual']),
  accrual_rule: accrualRuleSchema,
  approver_profile_ids: z.array(z.string()).optional(),
});

type PolicyFormData = z.infer<typeof policySchema>;

interface PolicyConfigurationFormProps {
  initialData?: Partial<CreateLeavePolicyInput | UpdateLeavePolicyInput>;
  onSave: (data: CreateLeavePolicyInput | UpdateLeavePolicyInput) => void;
  onCancel: () => void;
  isLoading?: boolean;
  mode: 'create' | 'edit';
}

// Import centralized leave types
import { LEAVE_TYPES } from '@/constants/leaveTypes';

const accrualMethods: { value: AccrualMethod; label: string; description: string }[] = [
  {
    value: 'does_not_accrue',
    label: 'Does not accrue',
    description: 'Leave balances are managed manually - no automatic accrual'
  },
  {
    value: 'hourly_worked',
    label: 'Hourly worked',
    description: 'Leave accrues based on hours worked by employees'
  },
  {
    value: 'fixed_accrual',
    label: 'Fixed accrual',
    description: 'Fixed amounts of leave released at regular intervals'
  },
];

// Helper function to sanitize initial data - convert nulls to undefined and parse JSON strings
const sanitizeInitialData = (data: any) => {
  if (!data) return {};
  
  const sanitized = { ...data };
  
  // Handle accrual_rule if it exists
  if (sanitized.accrual_rule) {
    const rule = { ...sanitized.accrual_rule };
    
    // Convert nulls to undefined for all fields
    Object.keys(rule).forEach(key => {
      if (rule[key] === null) {
        rule[key] = undefined;
      }
    });
    
    // Parse tenure_rules if it's a JSON string
    if (typeof rule.tenure_rules === 'string') {
      try {
        rule.tenure_rules = JSON.parse(rule.tenure_rules);
      } catch (e) {
        console.warn('[PolicyConfigurationForm] Failed to parse tenure_rules:', rule.tenure_rules);
        rule.tenure_rules = [];
      }
    }
    
    // Ensure negative_balance_action has a default value
    if (!rule.negative_balance_action) {
      rule.negative_balance_action = 'block_request';
    }
    
    // Convert boolean-like strings to actual booleans
    ['enable_pro_rata', 'enable_tenure_rules', 'allow_negative_balance'].forEach(field => {
      if (typeof rule[field] === 'string') {
        rule[field] = rule[field] === 'true';
      }
    });
    
    sanitized.accrual_rule = rule;
  }
  
  // Normalize legacy accrual methods to supported ones
  const allowedMethods = ['does_not_accrue', 'hourly_worked', 'fixed_accrual'];
  if (sanitized.accrual_method && !allowedMethods.includes(sanitized.accrual_method)) {
    sanitized.accrual_method = 'does_not_accrue';
  }
  
  // Convert top-level nulls to undefined
  Object.keys(sanitized).forEach(key => {
    if (sanitized[key] === null) {
      sanitized[key] = undefined;
    }
  });
  
  
  return sanitized;
};

export function PolicyConfigurationForm({ 
  initialData, 
  onSave, 
  onCancel, 
  isLoading = false,
  mode 
}: PolicyConfigurationFormProps) {
  const { toast } = useToast();
  const [tenureRules, setTenureRules] = useState<TenureRule[]>([]);
  
  // Memoize sanitized data to prevent excessive re-processing
  const sanitizedData = useMemo(() => {
    const result = sanitizeInitialData(initialData);
    console.log('[PolicyConfigurationForm] Data sanitized once:', result);
    return result;
  }, [initialData]);
  const form = useForm<PolicyFormData>({
    resolver: zodResolver(policySchema),
    defaultValues: {
      name: sanitizedData?.name || '',
      description: sanitizedData?.description || '',
      leave_type: sanitizedData?.leave_type || '',
      accrual_method: sanitizedData?.accrual_method || 'does_not_accrue',
      accrual_rule: {
        does_not_deduct: false,
        hourly_accrual_type: 'per_hour_worked',
        accrual_rate_type: 'hours',
        include_overtime_hours: false,
        threshold_leave_unit: 'hours',
        fixed_accrual_unit: 'days',
        fixed_accrual_frequency: 'yearly',
        fixed_accrual_release_type: 'hire_date',
        fixed_accrual_specific_date: '',
        fixed_accrual_pro_rata: false,
        pro_rata_calculation_type: 'days',
        pro_rata_rounding_direction: 'nearest',
        enable_pro_rata: true,
        pro_rata_start_date: '',
        enable_tenure_rules: false,
        tenure_rules: [],
        allow_negative_balance: false,
        negative_balance_action: 'block_request',
        enable_max_yearly_accrual: false,
        enable_balance_cap: false,
        rollover_type: 'rollover',
        rollover_amount_type: 'entire',
        rollover_date_type: 'hire_date',
        ...sanitizedData?.accrual_rule
      },
      approver_profile_ids: sanitizedData?.approver_profile_ids || []
    }
  });

  const watchedAccrualMethod = useWatch({ control: form.control, name: 'accrual_method' });
  const watchedHourlyAccrualType = useWatch({ control: form.control, name: 'accrual_rule.hourly_accrual_type' });
  const watchedFixedAccrualFrequency = useWatch({ control: form.control, name: 'accrual_rule.fixed_accrual_frequency' });
  const watchedFixedAccrualReleaseType = useWatch({ control: form.control, name: 'accrual_rule.fixed_accrual_release_type' });
  const watchedFixedAccrualProRata = useWatch({ control: form.control, name: 'accrual_rule.fixed_accrual_pro_rata' });
  const watchedProRataRoundingDirection = useWatch({ control: form.control, name: 'accrual_rule.pro_rata_rounding_direction' });
  const watchedEnableTenureRules = useWatch({ control: form.control, name: 'accrual_rule.enable_tenure_rules' });
  const watchedAllowNegativeBalance = useWatch({ control: form.control, name: 'accrual_rule.allow_negative_balance' });
  const watchedEnableMaxYearlyAccrual = useWatch({ control: form.control, name: 'accrual_rule.enable_max_yearly_accrual' });
  const watchedEnableBalanceCap = useWatch({ control: form.control, name: 'accrual_rule.enable_balance_cap' });
  const watchedRolloverType = useWatch({ control: form.control, name: 'accrual_rule.rollover_type' });
  const watchedRolloverAmountType = useWatch({ control: form.control, name: 'accrual_rule.rollover_amount_type' });
const watchedRolloverDateType = useWatch({ control: form.control, name: 'accrual_rule.rollover_date_type' });
const watchedAccrualRate = useWatch({ control: form.control, name: 'accrual_rule.accrual_rate' });
const watchedThresholdLeaveAmount = useWatch({ control: form.control, name: 'accrual_rule.threshold_leave_amount' });
const watchedThresholdHours = useWatch({ control: form.control, name: 'accrual_rule.threshold_hours_to_work' });
const watchedFixedAccrualAmount = useWatch({ control: form.control, name: 'accrual_rule.fixed_accrual_amount' });
const watchedMaxYearlyAccrualDays = useWatch({ control: form.control, name: 'accrual_rule.max_yearly_accrual_days' });

// Local string state for numeric inputs to allow free typing (commas, backspace)
const [accrualRateInput, setAccrualRateInput] = useState<string>('');
const [thresholdLeaveInput, setThresholdLeaveInput] = useState<string>('');
const [thresholdHoursInput, setThresholdHoursInput] = useState<string>('');
const [fixedAccrualInput, setFixedAccrualInput] = useState<string>('');
const [maxYearlyAccrualInput, setMaxYearlyAccrualInput] = useState<string>('');

useEffect(() => {
  const v = watchedAccrualRate;
  if (v === undefined || v === null) {
    if (accrualRateInput !== '') setAccrualRateInput('');
  } else {
    const str = String(v);
    if (str !== accrualRateInput) setAccrualRateInput(str);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [watchedAccrualRate]);

useEffect(() => {
  const v = watchedThresholdLeaveAmount;
  if (v === undefined || v === null) {
    if (thresholdLeaveInput !== '') setThresholdLeaveInput('');
  } else {
    const str = String(v);
    if (str !== thresholdLeaveInput) setThresholdLeaveInput(str);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [watchedThresholdLeaveAmount]);

useEffect(() => {
  const v = watchedThresholdHours;
  if (v === undefined || v === null) {
    if (thresholdHoursInput !== '') setThresholdHoursInput('');
  } else {
    const str = String(v);
    if (str !== thresholdHoursInput) setThresholdHoursInput(str);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [watchedThresholdHours]);

useEffect(() => {
  const v = watchedFixedAccrualAmount;
  if (v === undefined || v === null) {
    if (fixedAccrualInput !== '') setFixedAccrualInput('');
  } else {
    const str = String(v);
    if (str !== fixedAccrualInput) setFixedAccrualInput(str);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [watchedFixedAccrualAmount]);

useEffect(() => {
  const v = watchedMaxYearlyAccrualDays;
  if (v === undefined || v === null) {
    if (maxYearlyAccrualInput !== '') setMaxYearlyAccrualInput('');
  } else {
    const str = String(v);
    if (str !== maxYearlyAccrualInput) setMaxYearlyAccrualInput(str);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [watchedMaxYearlyAccrualDays]);

  // Tenure rule management
  const addTenureRule = useCallback(() => {
    const newRule: TenureRule = {
      years_of_service: 0,
      additional_days: 0
    };
    const updatedRules = [...tenureRules, newRule];
    setTenureRules(updatedRules);
    form.setValue('accrual_rule.tenure_rules', updatedRules);
  }, [tenureRules, form]);

  const removeTenureRule = useCallback((index: number) => {
    const updatedRules = tenureRules.filter((_, i) => i !== index);
    setTenureRules(updatedRules);
    form.setValue('accrual_rule.tenure_rules', updatedRules);
  }, [tenureRules, form]);

  const updateTenureRule = useCallback((index: number, field: keyof TenureRule, value: any) => {
    const updatedRules = [...tenureRules];
    updatedRules[index] = { ...updatedRules[index], [field]: value };
    setTenureRules(updatedRules);
    form.setValue('accrual_rule.tenure_rules', updatedRules);
  }, [tenureRules, form]);

  const onSubmit = useCallback((data: PolicyFormData) => {
    // Enhanced validation based on accrual method
    let validationError = '';
    
    switch (data.accrual_method) {
      case 'hourly_worked':
        if (data.accrual_rule.hourly_accrual_type === 'per_hour_worked') {
          if (!data.accrual_rule.accrual_rate || data.accrual_rule.accrual_rate <= 0) {
            validationError = "Please enter a valid accrual rate";
          }
        } else if (data.accrual_rule.hourly_accrual_type === 'per_threshold_worked') {
          if (!data.accrual_rule.threshold_hours_to_work || data.accrual_rule.threshold_hours_to_work <= 0 ||
              !data.accrual_rule.threshold_leave_amount || data.accrual_rule.threshold_leave_amount <= 0) {
            validationError = "Please enter valid threshold hours and leave amount";
          }
        }
        break;
      case 'fixed_accrual':
        if (!data.accrual_rule.fixed_accrual_amount || data.accrual_rule.fixed_accrual_amount <= 0) {
          validationError = "Please enter a valid fixed accrual amount";
        }
        // Phase 3: Validate pro-rata conflict for fixed accrual
        if (data.accrual_rule.fixed_accrual_pro_rata && data.accrual_rule.enable_pro_rata) {
          validationError = "Both fixed accrual pro-rata and general pro-rata cannot be enabled simultaneously. Please use only the fixed accrual pro-rata option for this policy.";
        }
        break;
    }
    
    if (validationError) {
      toast({
        title: "Validation Failed",
        description: validationError,
        variant: "destructive",
      });
      return;
    }
    
    // Transform form data to match backend expectations
    // Extract UI-only fields that should not be sent to database
    const { 
      enable_balance_cap, 
      enable_tenure_rules, 
      enable_max_yearly_accrual,
      ...accrualRuleForDB 
    } = data.accrual_rule;
    
    // Phase 3: For fixed accrual policies, ensure enable_pro_rata is false when fixed_accrual_pro_rata is true
    const finalAccrualRule = { ...accrualRuleForDB };
    if (data.accrual_method === 'fixed_accrual' && finalAccrualRule.fixed_accrual_pro_rata) {
      finalAccrualRule.enable_pro_rata = false;
    }
    
    const transformedData: CreateLeavePolicyInput | UpdateLeavePolicyInput = {
      name: data.name.trim(),
      description: data.description?.trim() || undefined,
      leave_type: data.leave_type,
      accrual_method: data.accrual_method,
      accrual_rule: {
        ...finalAccrualRule,
        // Handle balance cap - if disabled, set to null
        balance_cap_days: enable_balance_cap ? data.accrual_rule.balance_cap_days : null,
        // Handle max yearly accrual - if disabled, set to null
        max_yearly_accrual_days: enable_max_yearly_accrual ? data.accrual_rule.max_yearly_accrual_days : null,
        // Ensure tenure_rules is properly formatted for the backend
        tenure_rules: tenureRules.map(rule => ({
          years_of_service: Number(rule.years_of_service) || 0,
          additional_days: rule.additional_days ? Number(rule.additional_days) : undefined,
          multiplier: rule.multiplier ? Number(rule.multiplier) : undefined
        })).filter(rule => rule.years_of_service >= 0)
      },
      approver_profile_ids: data.approver_profile_ids?.filter(id => id.trim()) || []
    };
    
    // Add is_active field for edit mode
    if (mode === 'edit') {
      (transformedData as UpdateLeavePolicyInput).is_active = true;
    }
    
    onSave(transformedData);
  }, [mode, onSave, toast, tenureRules]);

  // Handle validation errors to surface them to user
  const onInvalid = useCallback((errors: any) => {
    const errorKeys = Object.keys(errors);
    const errorMessages = errorKeys.map(key => {
      const error = errors[key];
      return error?.message || key;
    });
    
    toast({
      title: "Please fix the following errors:",
      description: errorMessages.slice(0, 3).join(', ') + (errorMessages.length > 3 ? '...' : ''),
      variant: "destructive",
    });
  }, [toast]);

  return (
    <div dir="ltr" className="w-full">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="space-y-6 will-change-auto">
        {/* Basic Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Basic Details
            </CardTitle>
            <CardDescription>
              Define the fundamental information about this leave policy
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Policy Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Annual Leave Policy" {...field} />
                  </FormControl>
                  <FormDescription>
                    A clear, descriptive name for this leave policy
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Describe the purpose and details of this policy..."
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="leave_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Leave Type</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select leave type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {LEAVE_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
            </div>
          </CardContent>
        </Card>

        {/* Accrual Options */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Leave Accrual Options
            </CardTitle>
            <CardDescription>
              Choose how leave is earned and allocated for this policy
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <FormField
              control={form.control}
              name="accrual_method"
              render={({ field }) => (
                <FormItem>
                  <div className="w-full">
                      <FormControl>
                        <Tabs 
                          value={field.value} 
                          onValueChange={field.onChange}
                          className="w-full"
                        >
                          <TabsList className="flex flex-wrap w-full h-auto gap-1 p-1">
                            {accrualMethods.map((method) => (
                              <TabsTrigger
                                key={method.value}
                                value={method.value}
                                className={cn(
                                  "flex-grow min-w-[140px] px-3 py-2 text-sm font-medium transition-colors h-auto whitespace-normal text-center",
                                  "data-[state=active]:bg-primary data-[state=active]:text-primary-foreground",
                                  "data-[state=inactive]:bg-background data-[state=inactive]:text-foreground"
                                )}
                              >
                                {method.label}
                              </TabsTrigger>
                            ))}
                          </TabsList>

                          {/* Does Not Accrue Tab Content */}
                          <TabsContent value="does_not_accrue" className="mt-6">
                            <div className="p-4 bg-muted/30 rounded-lg">
                              <FormField
                                control={form.control}
                                name="accrual_rule.does_not_deduct"
                                render={({ field }) => (
                                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                    <FormControl>
                                      <Checkbox
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                        className="mt-1"
                                      />
                                    </FormControl>
                                    <div className="space-y-2 leading-none">
                                      <FormLabel className="text-sm font-medium">
                                        Leave does not deduct
                                      </FormLabel>
                                      <FormDescription className="text-sm text-muted-foreground">
                                        When selected, leave neither accrues nor deducts. Balance can still be edited manually from the team members profile.
                                      </FormDescription>
                                    </div>
                                  </FormItem>
                                )}
                              />
                            </div>
                          </TabsContent>

                          {/* Hourly Worked Tab Content */}
                          <TabsContent value="hourly_worked" className="mt-6">
                            <div className="pl-6 space-y-6 border-l-2 border-muted">
                              <FormItem>
                                <FormLabel>Accrual Type</FormLabel>
                                <FormControl>
                                  <div className="flex gap-2">
                                    <Button
                                      type="button"
                                      variant={form.watch('accrual_rule.hourly_accrual_type') === 'per_hour_worked' ? 'default' : 'outline'}
                                      size="sm"
                                      onClick={() => form.setValue('accrual_rule.hourly_accrual_type', 'per_hour_worked')}
                                      className="flex-1 h-auto py-3 px-4 justify-start"
                                    >
                                      <div className="text-left">
                                        <div className="font-medium">Per hour worked</div>
                                        <div className="text-xs opacity-70">Accrue leave for every hour worked</div>
                                      </div>
                                    </Button>
                                    <Button
                                      type="button"
                                      variant={form.watch('accrual_rule.hourly_accrual_type') === 'per_threshold_worked' ? 'default' : 'outline'}
                                      size="sm"
                                      onClick={() => form.setValue('accrual_rule.hourly_accrual_type', 'per_threshold_worked')}
                                      className="flex-1 h-auto py-3 px-4 justify-start"
                                    >
                                      <div className="text-left">
                                        <div className="font-medium">Per threshold worked</div>
                                        <div className="text-xs opacity-70">Accrue leave after working a certain number of hours</div>
                                      </div>
                                    </Button>
                                  </div>
                                </FormControl>
                                <FormMessage />
                              </FormItem>

                              {watchedHourlyAccrualType === 'per_hour_worked' && (
                                <div className="space-y-4 pl-6 border-l border-muted">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <FormField
                                      control={form.control}
                                      name="accrual_rule.accrual_rate"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>Accrual Rate</FormLabel>
                                          <FormControl>
                                             <Input 
                                               type="text"
                                               inputMode="decimal"
                                               pattern="[0-9]*[.,]?[0-9]*"
                                               placeholder="0.0385"
                                               value={accrualRateInput}
                                               onChange={(e) => setAccrualRateInput(e.target.value)}
                                               onBlur={(e) => {
                                                 const raw = e.target.value.trim();
                                                 if (raw === '') {
                                                   field.onChange(undefined);
                                                   return;
                                                 }
                                                 const normalized = raw.replace(',', '.');
                                                 const num = parseFloat(normalized);
                                                 field.onChange(Number.isNaN(num) ? undefined : num);
                                               }}
                                             />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                    
                                    <FormField
                                      control={form.control}
                                      name="accrual_rule.accrual_rate_type"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>Rate Type</FormLabel>
                                          <div className="flex items-center gap-2">
                                            <Select onValueChange={field.onChange} value={field.value}>
                                              <FormControl>
                                                <SelectTrigger className="w-[120px]">
                                                  <SelectValue placeholder="Select" />
                                                </SelectTrigger>
                                              </FormControl>
                                              <SelectContent>
                                                <SelectItem value="hours">Hours</SelectItem>
                                                <SelectItem value="percentage">%</SelectItem>
                                              </SelectContent>
                                            </Select>
                                            <span className="text-sm text-muted-foreground">per hour worked</span>
                                          </div>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                  </div>

                                   <FormField
                                     control={form.control}
                                     name="accrual_rule.include_overtime_hours"
                                     render={({ field }) => (
                                       <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                         <FormControl>
                                           <Checkbox
                                             checked={field.value}
                                             onCheckedChange={field.onChange}
                                           />
                                         </FormControl>
                                         <div className="space-y-1 leading-none">
                                           <FormLabel className="text-sm">
                                             Accrue leave on overtime hours in addition to ordinary hours
                                           </FormLabel>
                                         </div>
                                       </FormItem>
                                     )}
                                   />

                                   <Button
                                     type="button"
                                     variant="outline"
                                     size="sm"
                                     onClick={() => form.setValue('accrual_rule.enable_tenure_rules', true)}
                                     className="text-primary"
                                   >
                                     <Plus className="h-4 w-4 mr-2" />
                                     Add tenure-based rule
                                   </Button>
                                 </div>
                               )}

                              {watchedHourlyAccrualType === 'per_threshold_worked' && (
                                  <div className="space-y-4 pl-6 border-l border-muted">
                                    <div className="flex flex-wrap items-center gap-2 text-sm">
                                      <span>Leave will accrue at a rate of</span>
                                      <FormField
                                        control={form.control}
                                        name="accrual_rule.threshold_leave_amount"
                                        render={({ field }) => (
                                          <FormItem>
                                            <FormControl>
                                                 <Input 
                                                 type="text"
                                                 inputMode="decimal"
                                                 pattern="[0-9]*[.,]?[0-9]*"
                                                 placeholder="8"
                                                 className="w-20 h-8 text-center"
                                                 value={thresholdLeaveInput}
                                                 onChange={(e) => setThresholdLeaveInput(e.target.value)}
                                                 onBlur={(e) => {
                                                   const raw = e.target.value.trim();
                                                   if (raw === '') {
                                                     field.onChange(undefined);
                                                     return;
                                                   }
                                                   const normalized = raw.replace(',', '.');
                                                   const num = parseFloat(normalized);
                                                   field.onChange(Number.isNaN(num) ? undefined : num);
                                                 }}
                                              />
                                            </FormControl>
                                            <FormMessage />
                                          </FormItem>
                                        )}
                                      />
                                      <FormField
                                        control={form.control}
                                        name="accrual_rule.threshold_leave_unit"
                                        render={({ field }) => (
                                          <FormItem>
                                            <FormControl>
                                              <Select onValueChange={field.onChange} value={field.value}>
                                                <SelectTrigger className="w-24 h-8">
                                                  <SelectValue placeholder="Unit" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  <SelectItem value="hours">Hours</SelectItem>
                                                  <SelectItem value="days">Days</SelectItem>
                                                </SelectContent>
                                              </Select>
                                            </FormControl>
                                            <FormMessage />
                                          </FormItem>
                                        )}
                                      />
                                      <span>for every</span>
                                      <FormField
                                        control={form.control}
                                        name="accrual_rule.threshold_hours_to_work"
                                        render={({ field }) => (
                                          <FormItem>
                                            <FormControl>
                                                 <Input 
                                                 type="text"
                                                 inputMode="numeric"
                                                 pattern="[0-9]*"
                                                 placeholder="160"
                                                 className="w-20 h-8 text-center"
                                                 value={thresholdHoursInput}
                                                 onChange={(e) => setThresholdHoursInput(e.target.value)}
                                                 onBlur={(e) => {
                                                   const raw = e.target.value.trim();
                                                   if (raw === '') {
                                                     field.onChange(undefined);
                                                     return;
                                                   }
                                                   const num = parseInt(raw, 10);
                                                   field.onChange(Number.isNaN(num) ? undefined : num);
                                                 }}
                                              />
                                            </FormControl>
                                            <FormMessage />
                                          </FormItem>
                                        )}
                                      />
                                      <span>hours worked</span>
                                    </div>

                  <FormField
                    control={form.control}
                    name="accrual_rule.include_overtime_hours"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center space-x-3 space-y-0 py-2">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-sm cursor-pointer">
                            Include overtime hours in threshold calculation
                          </FormLabel>
                        </div>
                      </FormItem>
                    )}
                  />

                  {/* Phase 4: General pro-rata toggle for hourly worked */}
                  <FormField
                    control={form.control}
                    name="accrual_rule.enable_pro_rata"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 p-4 border rounded-lg">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-sm font-medium">
                            Enable pro-rata calculation for initial balance
                          </FormLabel>
                          <FormDescription className="text-xs">
                            Calculate proportional leave for employees based on their start date when assigning this policy
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => form.setValue('accrual_rule.enable_tenure_rules', true)}
                    className="text-primary"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add tenure-based rule
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

                          {/* Fixed Accrual Tab Content */}
                          <TabsContent value="fixed_accrual" className="mt-6">
                            <div className="pl-6 space-y-4 border-l-2 border-muted">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <FormField
                                  control={form.control}
                                  name="accrual_rule.fixed_accrual_amount"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Accrual Amount</FormLabel>
                                      <FormControl>
                                         <Input 
                                           type="text"
                                           inputMode="decimal"
                                           pattern="[0-9]*[.,]?[0-9]*"
                                           placeholder="25"
                                           value={fixedAccrualInput}
                                           onChange={(e) => setFixedAccrualInput(e.target.value)}
                                           onBlur={(e) => {
                                             const raw = e.target.value.trim();
                                             if (raw === '') {
                                               field.onChange(undefined);
                                               return;
                                             }
                                             const normalized = raw.replace(',', '.');
                                             const num = parseFloat(normalized);
                                             field.onChange(Number.isNaN(num) ? undefined : num);
                                           }}
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />

                                <FormField
                                  control={form.control}
                                  name="accrual_rule.fixed_accrual_unit"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Unit</FormLabel>
                                      <Select onValueChange={field.onChange} value={field.value}>
                                        <FormControl>
                                          <SelectTrigger>
                                            <SelectValue placeholder="Unit" />
                                          </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                          <SelectItem value="hours">Hours</SelectItem>
                                          <SelectItem value="days">Days</SelectItem>
                                        </SelectContent>
                                      </Select>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />

                                <FormField
                                  control={form.control}
                                  name="accrual_rule.fixed_accrual_frequency"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Release Frequency</FormLabel>
                                      <Select onValueChange={field.onChange} value={field.value}>
                                        <FormControl>
                                          <SelectTrigger>
                                            <SelectValue placeholder="Frequency" />
                                          </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                          <SelectItem value="weekly">Weekly</SelectItem>
                                          <SelectItem value="monthly">Monthly</SelectItem>
                                          <SelectItem value="quarterly">Quarterly</SelectItem>
                                          <SelectItem value="yearly">Yearly</SelectItem>
                                        </SelectContent>
                                      </Select>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              </div>

                              <FormField
                                control={form.control}
                                name="accrual_rule.fixed_accrual_release_type"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Release Timing</FormLabel>
                                    <FormControl>
                                      <RadioGroup 
                                        onValueChange={field.onChange} 
                                        value={field.value}
                                        className="space-y-2"
                                      >
                                        <div className="flex items-center space-x-2">
                                          <RadioGroupItem value="hire_date" id="hire_date" />
                                          <label htmlFor="hire_date" className="text-sm cursor-pointer">
                                            Based on employee's hire date
                                          </label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          <RadioGroupItem value="specific_date" id="specific_date" />
                                          <label htmlFor="specific_date" className="text-sm cursor-pointer">
                                            On a specific date
                                          </label>
                                        </div>
                                      </RadioGroup>
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />

                              {watchedFixedAccrualReleaseType === 'specific_date' && (
                                <FormField
                                  control={form.control}
                                  name="accrual_rule.fixed_accrual_specific_date"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Specific Date</FormLabel>
                                       <FormControl>
                                         <Input 
                                           type="date"
                                           className="w-48"
                                           {...field}
                                         />
                                       </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              )}

              {watchedFixedAccrualFrequency === 'yearly' && watchedFixedAccrualReleaseType === 'specific_date' && (
                <>
                  <FormField
                    control={form.control}
                    name="accrual_rule.fixed_accrual_pro_rata"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-sm">
                            Release pro-rata amounts for team members who join part-way through the accrual period
                          </FormLabel>
                          <FormDescription className="text-xs">
                            For fixed accrual policies with yearly frequency, calculate proportional leave based on when employees join during the year
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />

                  {watchedFixedAccrualProRata && (
                    <Accordion type="single" collapsible className="w-full">
                      <AccordionItem value="pro-rata-settings">
                        <AccordionTrigger className="text-sm hover:no-underline">
                          <span className="flex items-center gap-2">
                            <HelpCircle className="h-4 w-4" />
                            View all pro-rata settings
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="space-y-4 pt-4">
                          <div className="grid grid-cols-1 gap-4">
                            <FormField
                              control={form.control}
                              name="accrual_rule.pro_rata_calculation_type"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Calculate Pro-rata based on</FormLabel>
                                  <Select onValueChange={field.onChange} value={field.value}>
                                    <FormControl>
                                      <SelectTrigger className="w-48">
                                        <SelectValue placeholder="Select calculation type" />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      <SelectItem value="days">Days</SelectItem>
                                      <SelectItem value="hours">Hours</SelectItem>
                                      <SelectItem value="percentage">Percentage</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <FormDescription>
                                    Pro-rata leave = (yearly entitlement) × ({field.value === 'days' ? 'days' : field.value} in employment in leave year) ÷ ({field.value === 'days' ? 'days' : field.value} in year)
                                  </FormDescription>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={form.control}
                              name="accrual_rule.pro_rata_rounding_direction"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Round by</FormLabel>
                                  <Select onValueChange={field.onChange} value={field.value}>
                                    <FormControl>
                                      <SelectTrigger className="w-48">
                                        <SelectValue placeholder="Select rounding direction" />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      <SelectItem value="up">Rounding up</SelectItem>
                                      <SelectItem value="down">Rounding down</SelectItem>
                                      <SelectItem value="nearest">No rounding</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />


                            <div className="p-4 bg-muted rounded-lg">
                              <div className="text-sm space-y-2">
                                <h4 className="font-medium">Pro-rata Conditions:</h4>
                                <ul className="list-disc list-inside text-muted-foreground space-y-1 text-xs">
                                  <li>Leave policy must be configured with yearly accrual and specific date release</li>
                                  <li>Employee must have this policy assigned</li>
                                  <li>Employee must have their hire date entered</li>
                                  <li>Hire date must fall within the configured leave year</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  )}
                </>
              )}

                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => form.setValue('accrual_rule.enable_tenure_rules', true)}
                                className="text-primary"
                              >
                                <Plus className="h-4 w-4 mr-2" />
                                Add tenure-based rule
                              </Button>
                            </div>
                          </TabsContent>

                        </Tabs>
                      </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Tenure-Based Rules Management */}
            {watchedEnableTenureRules && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Tenure-Based Rules</CardTitle>
                  <CardDescription>
                    Different amounts of leave can be accrued depending on how long the team member has been with your organization. 
                    For this policy to function you will need to have your team member's hire date entered accurately in their employee profile.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {tenureRules.map((rule, index) => (
                    <div key={index} className="space-y-3 p-4 bg-muted/30 rounded-lg">
                      <div className="flex items-center gap-2 text-sm">
                        <span>After</span>
                         <Input
                           type="number"
                           step="1"
                           min="0"
                           placeholder="2"
                           value={rule.years_of_service || ''}
                           onChange={(e) => {
                             const value = e.target.value;
                             updateTenureRule(index, 'years_of_service', value === '' ? undefined : parseInt(value) || undefined);
                           }}
                          className="w-20 h-8"
                        />
                        <span>years from employee's hire date.</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeTenureRule(index)}
                          className="text-destructive hover:text-destructive ml-auto"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm">
                        <span>A total of</span>
                        <Input
                          type="number"
                          step="0.5"
                          min="0"
                          placeholder="5"
                          value={rule.additional_days || ''}
                          onChange={(e) => updateTenureRule(index, 'additional_days', parseFloat(e.target.value) || undefined)}
                          className="w-20 h-8"
                        />
                        <span>days will be released every year.</span>
                      </div>
                    </div>
                  ))}
                  
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addTenureRule}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add another rule
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Balance Management */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Balance Management</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Balance Options Toggles */}
                  <div className="flex flex-wrap gap-4">
                    <FormField
                      control={form.control}
                      name="accrual_rule.enable_max_yearly_accrual"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                          <FormControl>
                            <Switch checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                          <FormLabel className="text-sm font-medium">Max yearly accrual</FormLabel>
                        </FormItem>
                      )}
                    />
                    
                     <FormField
                       control={form.control}
                       name="accrual_rule.enable_balance_cap"
                       render={({ field }) => (
                         <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                           <FormControl>
                             <Switch 
                               checked={field.value} 
                               onCheckedChange={(checked) => {
                                 field.onChange(checked);
                                 if (checked && !form.getValues('accrual_rule.balance_cap_days')) {
                                   form.setValue('accrual_rule.balance_cap_days', 15);
                                 }
                               }} 
                             />
                           </FormControl>
                           <FormLabel className="text-sm font-medium">Set max balance</FormLabel>
                         </FormItem>
                       )}
                     />
                  </div>
                  
                  {/* Rollover or reset leave section */}
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-medium mb-2">Rollover or reset leave</h4>
                      <p className="text-sm text-muted-foreground">
                        Choose whether you want to rollover or reset the leave balance for this leave policy.
                      </p>
                    </div>
                    
                    <FormField
                      control={form.control}
                      name="accrual_rule.rollover_type"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant={field.value === 'rollover' ? 'default' : 'outline'}
                              onClick={() => field.onChange('rollover')}
                              className="flex-1"
                            >
                              Rollover
                            </Button>
                            <Button
                              type="button"
                              variant={field.value === 'reset' ? 'default' : 'outline'}
                              onClick={() => field.onChange('reset')}
                              className="flex-1"
                            >
                              Reset
                            </Button>
                          </div>
                        </FormItem>
                      )}
                    />

                    {watchedRolloverType === 'rollover' && (
                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name="accrual_rule.rollover_amount_type"
                          render={({ field }) => (
                            <FormItem>
                              <div className="flex gap-2">
                                <Button
                                  type="button"
                                  variant={field.value === 'entire' ? 'default' : 'outline'}
                                  onClick={() => field.onChange('entire')}
                                  size="sm"
                                >
                                  Rollover entire balance
                                </Button>
                                <Button
                                  type="button"
                                  variant={field.value === 'partial' ? 'default' : 'outline'}
                                  onClick={() => field.onChange('partial')}
                                  size="sm"
                                >
                                  Rollover part of balance
                                </Button>
                              </div>
                            </FormItem>
                          )}
                        />
                        
                        {watchedRolloverAmountType === 'partial' && (
                          <FormField
                            control={form.control}
                            name="accrual_rule.rollover_partial_amount"
                            render={({ field }) => (
                              <FormItem>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm">Maximum of</span>
                                  <FormControl>
                                    <Input 
                                      type="number" 
                                      step="0.5"
                                      placeholder="5"
                                      className="w-20"
                                      {...field}
                                      onChange={(e) => field.onChange(parseFloat(e.target.value) || undefined)}
                                    />
                                  </FormControl>
                                  <span className="text-sm">days</span>
                                </div>
                              </FormItem>
                            )}
                          />
                        )}
                      </div>
                    )}

                    <FormField
                      control={form.control}
                      name="accrual_rule.rollover_date_type"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <RadioGroup 
                              onValueChange={field.onChange} 
                              value={field.value}
                              className="space-y-2"
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="specific_date" id="specific_date" />
                                <label htmlFor="specific_date" className="text-sm">on a specific date</label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="hire_date" id="hire_date" />
                                <label htmlFor="hire_date" className="text-sm">the employee's hire date</label>
                              </div>
                            </RadioGroup>
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    {watchedRolloverDateType === 'specific_date' && (
                      <FormField
                        control={form.control}
                        name="accrual_rule.rollover_specific_date"
                        render={({ field }) => (
                          <FormItem>
                            <Popover>
                              <PopoverTrigger asChild>
                                <FormControl>
                                  <Button
                                    variant="outline"
                                    className={cn(
                                      "w-[240px] pl-3 text-left font-normal",
                                      !field.value && "text-muted-foreground"
                                    )}
                                  >
                                    {field.value ? (
                                      format(new Date(field.value), "PPP")
                                    ) : (
                                      <span>Pick a date</span>
                                    )}
                                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                  </Button>
                                </FormControl>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <UICalendar
                                  mode="single"
                                  selected={field.value ? new Date(field.value) : undefined}
                                  onSelect={(date) => field.onChange(date ? format(date, 'yyyy-MM-dd') : undefined)}
                                  initialFocus
                                  className={cn("p-3 pointer-events-auto")}
                                />
                              </PopoverContent>
                            </Popover>
                          </FormItem>
                        )}
                      />
                    )}
                  </div>
                  
                  {/* Max yearly accrual section */}
                  {watchedEnableMaxYearlyAccrual && (
                    <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
                      <h4 className="text-sm font-medium">Max yearly accrual</h4>
                      <FormField
                        control={form.control}
                        name="accrual_rule.max_yearly_accrual_days"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex items-center gap-2 text-sm">
                              <span>Accrue a maximum of</span>
                              <FormControl>
                                <Input 
                                  type="text"
                                  inputMode="decimal"
                                  pattern="[0-9]*[.,]?[0-9]*"
                                  step="0.5"
                                  placeholder="10"
                                  className="w-16"
                                  value={maxYearlyAccrualInput}
                                  onChange={(e) => setMaxYearlyAccrualInput(e.target.value)}
                                  onBlur={(e) => {
                                    const raw = e.target.value.trim();
                                    if (raw === '') {
                                      field.onChange(undefined);
                                      return;
                                    }
                                    const normalized = raw.replace(',', '.');
                                    const num = parseFloat(normalized);
                                    field.onChange(Number.isNaN(num) ? undefined : num);
                                  }}
                                />
                              </FormControl>
                              <span>days in a given year. Resets on January 1.</span>
                            </div>
                          </FormItem>
                        )}
                      />
                    </div>
                  )}
                  
                   {/* Set max balance section */}
                   {watchedEnableBalanceCap && (
                     <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
                       <h4 className="text-sm font-medium">Set max balance</h4>
                       <FormField
                         control={form.control}
                         name="accrual_rule.balance_cap_days"
                         render={({ field }) => (
                           <FormItem>
                             <div className="flex items-center gap-2 text-sm">
                               <span>Stop accruing leave at</span>
                               <FormControl>
                                 <Input 
                                   type="number" 
                                   step="0.5"
                                   placeholder="15"
                                   className="w-16"
                                   value={field.value || ''}
                                   onChange={(e) => {
                                     const value = e.target.value;
                                     if (value === '') {
                                       field.onChange(null);
                                     } else {
                                       const numValue = parseFloat(value);
                                       field.onChange(isNaN(numValue) ? null : numValue);
                                     }
                                   }}
                                 />
                               </FormControl>
                               <span>days</span>
                             </div>
                           </FormItem>
                         )}
                       />
                     </div>
                   )}
                  
                  <FormField
                    control={form.control}
                    name="accrual_rule.allow_negative_balance"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">Allow Negative Balance</FormLabel>
                          <FormDescription>
                            Allow employees to request more leave than they have available
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {watchedAllowNegativeBalance && (
                    <div className="pl-4 border-l-2 border-muted space-y-4">
                      <FormField
                        control={form.control}
                        name="accrual_rule.max_negative_days"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center gap-2">
                              Set a minimum allowable balance of
                              <HelpCircle className="h-4 w-4 text-muted-foreground" />
                            </FormLabel>
                            <div className="flex items-center gap-2">
                              <FormControl>
                                <Input 
                                  type="number" 
                                  step="0.5"
                                  placeholder="5"
                                  className="w-24"
                                  {...field}
                                  onChange={(e) => {
                                    const value = parseFloat(e.target.value);
                                    field.onChange(isNaN(value) ? undefined : Math.abs(value));
                                  }}
                                  value={field.value !== undefined && field.value !== null ? Math.abs(field.value) : ''}
                                />
                              </FormControl>
                              <span className="text-sm text-muted-foreground">days</span>
                            </div>
                            <FormDescription>
                              The system checks against current balance, not future projected accruals
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="accrual_rule.negative_balance_action"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>When a request exceeds the available balance:</FormLabel>
                            <FormControl>
                              <RadioGroup 
                                onValueChange={field.onChange} 
                                value={field.value}
                                className="grid grid-cols-1 gap-4"
                              >
                                <div className="flex items-start space-x-3 rounded-lg border p-4">
                                  <RadioGroupItem value="block_request" id="block_request" className="mt-1" />
                                  <div className="grid gap-1.5 leading-none">
                                    <label 
                                      htmlFor="block_request" 
                                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                    >
                                      Block Request
                                    </label>
                                    <p className="text-xs text-muted-foreground">
                                      The employee will be prevented from submitting the request. Managers will not be notified and cannot override this.
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-start space-x-3 rounded-lg border p-4">
                                  <RadioGroupItem value="allow_and_notify" id="allow_and_notify" className="mt-1" />
                                  <div className="grid gap-1.5 leading-none">
                                    <label 
                                      htmlFor="allow_and_notify" 
                                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                    >
                                      Allow and Notify
                                    </label>
                                    <p className="text-xs text-muted-foreground">
                                      The employee can submit the request, but a warning will be sent to the approving manager.
                                    </p>
                                  </div>
                                </div>
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                     </div>
                  )}
                </CardContent>
              </Card>
        </CardContent>
      </Card>

      {/* Form Actions */}
      <div className="flex justify-end gap-4 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button 
          type="submit" 
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : mode === 'create' ? 'Create Policy' : 'Update Policy'}
        </Button>
        </div>
        </form>
      </Form>
    </div>
  );
}